import { useMemo } from "react";
import { FormikErrors } from "formik";
import { TFunction } from "i18next";

export interface ValidationError {
  field: string;
  fieldPath: string;
  message: string;
  tabName?: string;
  sectionPath?: string;
}

export interface TabConfig {
  [tabName: string]: {
    title: string;
    fields: {
      [fieldPath: string]: {
        label: string;
        section?: string;
      };
    };
  };
}

/**
 * Maps technical field paths to their corresponding translation keys
 * This ensures validation errors display user-friendly field names
 */
const FIELD_TRANSLATION_MAP: Record<string, string> = {
  // Proposal Data section
  "proposalData.proposalType": "qtg.offer.proposalData.proposalType",
  "proposalData.proposalContents": "qtg.offer.proposalData.proposalContents",
  "proposalData.proposalTemplate": "qtg.offer.proposalData.proposalTemplate",
  "proposalData.bulkTaskId": "qtg.offer.proposalData.bulkTaskId",
  "proposalData.validDateTill": "qtg.offer.proposalData.validDateTill",

  // Vehicle Data section
  "vehicleData.licensePlateNumber": "qtg.offer.vehicleData.licensePlateNumber",
  "vehicleData.technicalPassportNumber":
    "qtg.offer.vehicleData.technicalPassportNumber",
  "vehicleData.vehicleCetificateNumber":
    "qtg.offer.vehicleData.vehicleCetificateNumber",
  "vehicleData.vehicleMake": "qtg.offer.vehicleData.vehicleMake",
  "vehicleData.vehicleModel": "qtg.offer.vehicleData.vehicleModel",
  "vehicleData.vehicleModification":
    "qtg.offer.vehicleData.vehicleModification",
  "vehicleData.engineCapacity": "qtg.offer.vehicleData.engineCapacity",
  "vehicleData.vin": "qtg.offer.vehicleData.vin",
  "vehicleData.power": "qtg.offer.vehicleData.power",
  "vehicleData.vehicleValue": "qtg.offer.vehicleData.vehicleValue",
  "vehicleData.maximumGrossWeight": "qtg.offer.vehicleData.maximumGrossWeight",
  "vehicleData.objectGroup": "qtg.offer.vehicleData.objectGroup",
  "vehicleData.productionYear": "qtg.offer.vehicleData.productionYear",
  "vehicleData.objectUsage": "qtg.offer.vehicleData.objectUsage",
  "vehicleData.abroadUse": "qtg.offer.vehicleData.abroadUse",
  "vehicleData.seats": "qtg.offer.vehicleData.seats",
  "vehicleData.vehicleBodyTypeCode":
    "qtg.offer.vehicleData.vehicleBodyTypeCode",
  "vehicleData.vehiclePowerTypeCode":
    "qtg.offer.vehicleData.vehiclePowerTypeCode",
  "vehicleData.ownerCode": "qtg.offer.vehicleData.ownerCode",
  "vehicleData.ownerName": "qtg.offer.vehicleData.ownerName",
  "vehicleData.leasingCompany": "qtg.offer.vehicleData.leasingCompany",
  "vehicleData.glassInsurance": "qtg.offer.vehicleData.glassInsurance",
  "vehicleData.deductible": "qtg.offer.vehicleData.deductible",

  // Client Data section
  "clientData.clientType": "qtg.offer.clientData.clientType",
  "clientData.clientName": "qtg.offer.clientData.clientName",
  "clientData.clientCode": "qtg.offer.clientData.clientCode",
  "clientData.clientEmail": "qtg.offer.clientData.clientEmail",
  "clientData.licenseDate": "qtg.offer.clientData.licenseDate",
  "clientData.age": "qtg.offer.clientData.age",
  "clientData.region": "qtg.offer.clientData.region",
  "clientData.address": "qtg.offer.clientData.address",
  "clientData.postalCode": "qtg.offer.clientData.postalCode",
  "clientData.phone": "qtg.offer.clientData.phone",
  "clientData.drivingExperience": "qtg.offer.clientData.drivingExperience",
  "clientData.bonusMalus": "qtg.offer.clientData.bonusMalus",
  "clientData.socialDiscount": "qtg.offer.clientData.socialDiscount",
  "clientData.territory": "qtg.offer.clientData.territory",

  // Bill Data section
  "billData.billPayerType": "qtg.offer.billData.billPayerType",
  "billData.billPayerName": "qtg.offer.billData.billPayerName",
  "billData.billPayerCode": "qtg.offer.billData.billPayerCode",
  "billData.billPayerEmail": "qtg.offer.billData.billPayerEmail",
  "billData.billPayerPhone": "qtg.offer.billData.billPayerPhone",
  "billData.billPayerAddress": "qtg.offer.billData.billPayerAddress",

  // Additional Data section
  "additionalData.accountManager": "qtg.offer.additionalData.accountManager",
  "additionalData.salesPerson": "qtg.offer.additionalData.salesPerson",
  "additionalData.referenceNumber": "qtg.offer.additionalData.referenceNumber",
  "additionalData.riskAssessment": "qtg.offer.additionalData.riskAssessment",
  "additionalData.notes": "qtg.offer.additionalData.notes",

  // Verification Data section
  "verificationData.verificationStatus":
    "qtg.offer.verificationData.verificationStatus",
  "verificationData.documentType": "qtg.offer.verificationData.documentType",
  "verificationData.documentNumber":
    "qtg.offer.verificationData.documentNumber",
  "verificationData.issueDate": "qtg.offer.verificationData.issueDate",
  "verificationData.expiryDate": "qtg.offer.verificationData.expiryDate",
  "verificationData.issuingAuthority":
    "qtg.offer.verificationData.issuingAuthority",
  "verificationData.verificationMethod":
    "qtg.offer.verificationData.verificationMethod",

  // Policy Holder
  "policyHolder.firstName": "qtg.offer.policyHolder.firstName",
  "policyHolder.lastName": "qtg.offer.policyHolder.lastName",
  "policyHolder.code": "qtg.offer.policyHolder.code",
  "policyHolder.phone": "qtg.offer.policyHolder.phone",
  "policyHolder.email": "qtg.offer.policyHolder.email",
  "policyHolder.city": "qtg.offer.policyHolder.city",
  "policyHolder.street": "qtg.offer.policyHolder.street",
  "policyHolder.house": "qtg.offer.policyHolder.house",
  "policyHolder.flat": "qtg.offer.policyHolder.flat",
  "policyHolder.postalCode": "qtg.offer.policyHolder.postalCode",
};

/**
 * Hook for handling validation errors, extracting them from Formik errors into a structured format
 * @param tabConfig Configuration mapping field paths to tabs and sections
 * @returns Utility functions for validation errors
 */
export function useValidationErrors(tabConfig?: TabConfig) {
  const fieldMap = useMemo(() => {
    // Create a map of field paths to tab names and section paths
    const map: Record<
      string,
      { tabName: string; fieldLabel: string; sectionPath?: string }
    > = {};

    // Handle null or undefined tabConfig
    if (!tabConfig) {
      return map;
    }

    Object.entries(tabConfig).forEach(([tabName, tabData]) => {
      // Check if tabData exists and has fields
      if (tabData && tabData.fields) {
        Object.entries(tabData.fields).forEach(([fieldPath, fieldConfig]) => {
          map[fieldPath] = {
            tabName,
            fieldLabel: fieldConfig.label,
            sectionPath: fieldConfig.section,
          };
        });
      }
    });

    return map;
  }, [tabConfig]);

  /**
   * Get a user-friendly field label for display in validation messages
   * @param fieldPath The technical field path (e.g. "proposalData.saleChannel")
   * @param t Translation function to use
   * @returns User-friendly translated field name
   */
  const getFieldLabel = (fieldPath: string, t: TFunction): string => {
    // First try the translation map
    if (FIELD_TRANSLATION_MAP[fieldPath]) {
      return t(FIELD_TRANSLATION_MAP[fieldPath]);
    }

    // Look for nested fields (array items)
    const arrayMatch = fieldPath.match(/(.+)\[(\d+)\]\.(.+)/);
    if (arrayMatch) {
      const [, arrayPath, index, property] = arrayMatch;
      const arrayTranslationKey = arrayPath
        ? FIELD_TRANSLATION_MAP[arrayPath]
        : undefined;
      if (arrayTranslationKey) {
        return `${t(arrayTranslationKey)} (${Number(index) + 1}) - ${property}`;
      }
    }

    // For nested fields without direct mappings
    const parts = fieldPath.split(".");
    if (parts.length > 1) {
      const parentPath = parts.slice(0, parts.length - 1).join(".");
      const fieldName = parts[parts.length - 1];

      const parentTranslationKey = FIELD_TRANSLATION_MAP[parentPath];
      if (parentTranslationKey) {
        return `${t(parentTranslationKey)}: ${fieldName}`;
      }
    }

    // If all else fails, format the technical field name to be more readable
    const lastPart = fieldPath.split(".").pop() || fieldPath;
    return lastPart
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase());
  };

  /**
   * Extract structured validation errors from Formik errors object
   * @param errors Formik errors object
   * @param t Translation function
   * @returns Array of validation errors with field info and messages
   */
  const extractValidationErrors = (
    errors: FormikErrors<any>,
    t: TFunction
  ): ValidationError[] => {
    const validationErrors: ValidationError[] = [];

    // If errors is null or undefined, return empty array
    if (!errors) {
      return validationErrors;
    }

    /**
     * Process errors recursively to extract all nested errors
     * @param errObj Error object or nested error object
     * @param parentPath Current path in the error object hierarchy
     */
    const processErrors = (errObj: any, parentPath: string = "") => {
      if (!errObj) return;

      Object.entries(errObj).forEach(([key, value]) => {
        const path = parentPath ? `${parentPath}.${key}` : key;

        if (typeof value === "string") {
          // This is an error message
          const fieldInfo = fieldMap[path] || { tabName: "", fieldLabel: path };
          const friendlyFieldName = getFieldLabel(path, t);

          validationErrors.push({
            field: friendlyFieldName,
            fieldPath: path,
            message: value,
            tabName: fieldInfo.tabName,
            sectionPath: fieldInfo.sectionPath,
          });
        } else if (value && typeof value === "object") {
          // This is a nested object with more errors or an array
          if (Array.isArray(value)) {
            // Handle array errors
            value.forEach((item, index) => {
              if (typeof item === "string") {
                const arrayPath = `${path}[${index}]`;
                const fieldInfo = fieldMap[path] || {
                  tabName: "",
                  fieldLabel: path,
                };
                const friendlyFieldName = getFieldLabel(path, t);

                validationErrors.push({
                  field: `${friendlyFieldName} (${index + 1})`,
                  fieldPath: arrayPath,
                  message: item,
                  tabName: fieldInfo.tabName,
                  sectionPath: fieldInfo.sectionPath,
                });
              } else if (item && typeof item === "object") {
                processErrors(item, `${path}[${index}]`);
              }
            });
          } else {
            // Handle object errors
            processErrors(value, path);
          }
        }
      });
    };

    processErrors(errors);
    return validationErrors;
  };

  return {
    extractValidationErrors,
  };
}
